// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this file.
import React, { useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { resolveLoanBookSelection, getDisplayLoanBooks } from '../../utils/loanBookResolution';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { useBookChurnReport } from '../../hooks/useBookChurnReport';
import { useCompanyDataYearRange } from '../../hooks/useCompanyDataYearRange';
import { formatCurrency } from '../../utils/formatters';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from 'recharts';
import { HeartPulse } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { SkeletonReportPage } from '../../components/common/SkeletonLoader';

// Helper to calculate broker percentages for Book Health Report
interface BrokerBreakdown {
  brokerId: string;
  brokerDisplay: string;
  totalUpfronts: number;
  trueChurnCount: number;   // genuinely lost clients
  retainedCount: number;    // discharges retained via a superseding loan
  churnRate: number;        // trueChurnCount / totalUpfronts (%)
  clawbackPeriodChurnCount: number;
  trailHeadwindCents: number;
}

const calculateBrokerPercentages = (brokers: BrokerBreakdown[]) => {
  const totalUpfronts = brokers.reduce((sum, b) => sum + b.totalUpfronts, 0);
  return brokers.map(broker => ({
    ...broker,
    percentOfTotal: totalUpfronts > 0 ? (broker.totalUpfronts / totalUpfronts) * 100 : 0,
  }));
};

/**
 * BookHealthReportPage - Track and analyze loan discharges (book health)
 *
 * Displays Book Health metrics from commission discharge data:
 * - Net Book Growth Rate (new settlements vs genuinely lost clients)
 * - Churn Rate per broker (true churn / upfronts; retained clients excluded)
 * - Retained vs True churn split (a client kept via a superseding loan is Retained)
 * - Clawback risk (true-churn discharges within the 24-month clawback period)
 * - Trail headwind (actual clawback amounts)
 * - Tenure cohort analysis (insurance-style persistency)
 *
 * KEY INSIGHT: a discharge with a confirmed refinance match is a RETAINED client,
 * not churn. The backend splits discharges into Retained vs True churn / Lost.
 */

const BookHealthReportPage: React.FC = () => {
  const { userData, loanBooks = [] } = useOutletContext<any>();
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [selectedBroker, setSelectedBroker] = useState('all');
  const hasEntityLoanBooks = useMemo(() =>
    (loanBooks as ResolvedLoanBook[]).some((lb: ResolvedLoanBook) => lb.type === 'entity'),
    [loanBooks]
  );
  const [selectedLoanBook, setSelectedLoanBook] = useState('all');
  const resolvedParams = useMemo(
    () => resolveLoanBookSelection(selectedLoanBook, loanBooks),
    [selectedLoanBook, loanBooks]
  );

  // ✅ DYNAMIC YEAR RANGE: Fetch from BigQuery for year dropdown population
  const { earliestYear, latestYear } = useCompanyDataYearRange({
    companyId: userData?.companyId,
  });

  const {
    netBookGrowthRate,
    totalNewClients,
    totalDischarged,
    trueChurnCount,
    retainedCount,
    churnRate,
    activeLoans,
    clawbackPeriodChurnCount,
    matureLoanChurnCount,
    clawbackPeriodChurnPct,
    totalTrailHeadwindCents,
    projectedAnnualTrailImpactCents,
    monthlyTrend,
    brokerBreakdown,
    churnByTenureCohort,
    refinanceEvents,
    loading,
    error,
  } = useBookChurnReport({
    companyId: userData?.companyId || '',
    year: yearFilter,
    yearType: 'calendar',
    brokerId: selectedBroker === 'all' ? undefined : selectedBroker,
    entityCode: resolvedParams.entityCode,
  });

  // Transform monthly trend for chart - show upfronts vs discharges as positive values
  const chartData = useMemo(() => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dataByMonth = new Map(monthlyTrend.map(m => [m.month, m]));

    return monthNames.map((monthName, idx) => {
      const monthKey = `${yearFilter}-${String(idx + 1).padStart(2, '0')}`;
      const data = dataByMonth.get(monthKey);

      return {
        month: monthName,
        upfronts: data?.totalUpfronts || 0,
        trueChurn: data?.trueChurnCount || 0,
        retained: data?.retainedCount || 0,
        netGrowth: data?.netGrowth || 0,
      };
    });
  }, [monthlyTrend, yearFilter]);

  // Get unique brokers for filter dropdown
  const availableBrokers = useMemo(() => {
    return brokerBreakdown.map(b => ({ id: b.brokerId, name: b.brokerDisplay }));
  }, [brokerBreakdown]);

  // Generate year options for dropdown (newest first)
  // ✅ FIX (2026-01-05): Always include current year even if no data exists yet
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const dynamicLatestYear = Math.max(latestYear ?? currentYear, currentYear);
    const dynamicEarliestYear = earliestYear ?? (currentYear - 4);
    const years = [];
    for (let year = dynamicLatestYear; year >= dynamicEarliestYear; year--) {
      years.push(year);
    }
    return years;
  }, [earliestYear, latestYear]);

  // Filter broker breakdown if specific broker selected
  const displayedBrokers = useMemo(() => {
    const filtered = selectedBroker === 'all'
      ? brokerBreakdown
      : brokerBreakdown.filter(b => b.brokerId === selectedBroker);

    // Add percentage of total column
    return calculateBrokerPercentages(filtered);
  }, [brokerBreakdown, selectedBroker]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-900 text-white p-4 rounded-lg shadow-xl border border-gray-700">
          <p className="font-bold text-sm mb-2">{data.month} {yearFilter}</p>
          <p className="text-green-400 font-semibold">
            New Settlements: {data.upfronts}
          </p>
          <p className="text-red-400 font-semibold">
            Lost (true churn): {data.trueChurn}
          </p>
          <p className="text-blue-400 font-semibold">
            Retained: {data.retained}
          </p>
          <p className={`font-semibold ${data.netGrowth >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
            Net Growth: {data.netGrowth >= 0 ? '+' : ''}{data.netGrowth}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <SkeletonReportPage statCards={4} statColumns={4} tableColumns={8} />;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
          <h3 className="text-red-300 font-semibold mb-2">Error Loading Data</h3>
          <p className="text-red-200">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={HeartPulse}
        title="Book Health Report"
        subtitle="Track retained clients vs true churn. Are you growing your book, or losing clients you don't win back?"
      />

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
        <h2 className="text-lg font-semibold mb-4 text-white">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Year</label>
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(parseInt(e.target.value))}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Broker</label>
            <select
              value={selectedBroker}
              onChange={(e) => setSelectedBroker(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Brokers</option>
              {availableBrokers.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          {/* Loan Book Selector — unified-mode companies only */}
          {hasEntityLoanBooks && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Loan Book</label>
              <select
                value={selectedLoanBook}
                onChange={(e) => setSelectedLoanBook(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Loan Books</option>
                {getDisplayLoanBooks(loanBooks as ResolvedLoanBook[]).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-end">
            <div className="w-full text-sm text-gray-400 bg-gray-700/50 rounded-lg px-4 py-2.5 border border-gray-600">
              Active Loans: <span className="text-white font-semibold">{activeLoans.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Net Book Growth */}
        <div className={`rounded-lg shadow-lg p-6 border ${netBookGrowthRate >= 0 ? 'bg-green-900/20 border-green-700' : 'bg-red-900/20 border-red-700'}`}>
          <h3 className="text-sm font-medium text-gray-400 mb-2">Net Book Growth</h3>
          <p className={`text-4xl font-bold ${netBookGrowthRate >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {netBookGrowthRate >= 0 ? '+' : ''}{netBookGrowthRate}%
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {totalNewClients} new - {trueChurnCount} lost
          </p>
        </div>

        {/* Churn Rate */}
        <div className={`rounded-lg shadow-lg p-6 border ${churnRate <= 25 ? 'bg-gray-800 border-gray-700' : 'bg-yellow-900/20 border-yellow-700'}`}>
          <h3 className="text-sm font-medium text-gray-400 mb-2">Churn Rate</h3>
          <p className={`text-4xl font-bold ${churnRate <= 25 ? 'text-white' : 'text-yellow-400'}`}>
            {churnRate}%
          </p>
          <p className="text-xs text-gray-500 mt-1">
            <span className="text-green-400 font-semibold">{retainedCount} retained</span>
            {' '}of {totalDischarged} discharged &middot; benchmark &lt;25%
          </p>
        </div>

        {/* Clawback Risk */}
        <div className={`rounded-lg shadow-lg p-6 border ${clawbackPeriodChurnCount <= 5 ? 'bg-gray-800 border-gray-700' : 'bg-red-900/20 border-red-700'}`}>
          <h3 className="text-sm font-medium text-gray-400 mb-2">Clawback Risk</h3>
          <p className={`text-4xl font-bold ${clawbackPeriodChurnCount <= 5 ? 'text-white' : 'text-red-400'}`}>
            {clawbackPeriodChurnCount}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Lost loans within 24-month clawback period
          </p>
        </div>

        {/* Total Clawback */}
        <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
          <h3 className="text-sm font-medium text-gray-400 mb-2">Total Clawback</h3>
          <p className="text-4xl font-bold text-orange-400">
            {formatCurrency(totalTrailHeadwindCents)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Commission clawed back from discharged loans
          </p>
        </div>
      </div>

      {/* Monthly Trend Chart */}
      <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700 relative overflow-hidden">
        {/* Subtle radial gradient backdrop - green tint for health report */}
        <div
          className="absolute inset-0 opacity-10"
          style={{ background: 'radial-gradient(circle at 50% 0%, #10b98140 0%, transparent 70%)' }}
        />

        <div className="relative z-10 flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-white">Settlements vs Lost Clients</h2>
            <p className="text-sm text-gray-400 mt-1">Monthly comparison for {yearFilter} - Green bars should exceed red for healthy growth (retained clients are not counted as lost)</p>
          </div>
          <div className="flex gap-4 text-sm">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 bg-green-500 rounded"></span>
              <span className="text-gray-400">New Settlements</span>
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 bg-red-500 rounded"></span>
              <span className="text-gray-400">Lost (true churn)</span>
            </span>
          </div>
        </div>

        {chartData.length > 0 ? (
          <div className="relative z-10">
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chartData} margin={{ top: 25, right: 30, left: 20, bottom: 5 }}>
                <defs>
                  <linearGradient id="gradientUpfronts" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22C55E" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#22C55E" stopOpacity={0.4}/>
                  </linearGradient>
                  <linearGradient id="gradientDischarges" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0.4}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#344155" opacity={0.3} />
                <XAxis dataKey="month" stroke="#8892b0" fontSize={12} />
                <YAxis stroke="#8892b0" fontSize={12} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                <Bar
                  dataKey="upfronts"
                  fill="url(#gradientUpfronts)"
                  radius={[4, 4, 0, 0]}
                  name="New Settlements"
                />
                <Bar
                  dataKey="trueChurn"
                  fill="url(#gradientDischarges)"
                  radius={[4, 4, 0, 0]}
                  name="Lost (true churn)"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="relative z-10 flex items-center justify-center h-64 text-gray-500">
            <p>No data available for {yearFilter}</p>
          </div>
        )}
      </div>

      {/* Broker Breakdown Table */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">Broker Performance: Growth vs Churn</h2>
          <p className="text-sm text-gray-400 mt-1">Identify brokers who are building their book vs losing clients to true churn</p>
        </div>

        {displayedBrokers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900 border-b border-gray-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Broker</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Total Upfronts</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">% of Total</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Retained</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Lost (True Churn)</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Churn Rate</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Clawback Risk</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Clawback Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {displayedBrokers.map((broker, idx) => {
                  const isHighConcentration = broker.percentOfTotal > 25;
                  return (
                    <tr
                      key={idx}
                      className={`hover:bg-gray-700 transition-colors ${broker.churnRate > 40 || isHighConcentration ? 'bg-red-900/10' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white">{broker.brokerDisplay}</span>
                          {isHighConcentration && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                              High Risk
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-300">{broker.totalUpfronts}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-semibold ${isHighConcentration ? 'text-yellow-400' : 'text-gray-300'}`}>
                            {broker.percentOfTotal.toFixed(1)}%
                          </span>
                          {/* Visual percentage bar */}
                          <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${isHighConcentration ? 'bg-yellow-400' : 'bg-blue-500'}`}
                              style={{ width: `${Math.min(broker.percentOfTotal, 100)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-green-400 font-semibold">{broker.retainedCount}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-red-400">{broker.trueChurnCount}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-semibold ${broker.churnRate > 40 ? 'text-red-400' : broker.churnRate > 25 ? 'text-yellow-400' : 'text-green-400'}`}>
                          {broker.churnRate}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm ${broker.clawbackPeriodChurnCount > 2 ? 'text-red-400 font-semibold' : 'text-gray-300'}`}>
                          {broker.clawbackPeriodChurnCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-orange-400">
                          {formatCurrency(broker.trailHeadwindCents)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            No broker data available for the selected filters
          </div>
        )}
      </div>

      {/* Tenure Cohort Analysis */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">When Do Your Loans Churn?</h2>
          <p className="text-sm text-gray-400 mt-1">Cohort analysis by loan age at discharge (insurance-style persistency)</p>
        </div>

        {churnByTenureCohort.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900 border-b border-gray-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Loan Age Cohort</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Churned Count</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Avg Clawback</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Risk Level</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {churnByTenureCohort.map((cohort, idx) => {
                  const isHighRisk = cohort.cohort.includes('0-12') || cohort.cohort.includes('12-24');
                  return (
                    <tr key={idx} className={`hover:bg-gray-700 transition-colors ${isHighRisk ? 'bg-red-900/10' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-white">{cohort.cohort}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-300">{cohort.churnedCount}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-orange-400">
                          {formatCurrency(cohort.avgTrailLostCents)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isHighRisk ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900/50 text-red-300">
                            Clawback Period
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/50 text-green-300">
                            Mature
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            No cohort data available
          </div>
        )}
      </div>

      {/* Discharged Loans Detail Table */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">Discharged Loans ({yearFilter})</h2>
          <p className="text-sm text-gray-400 mt-1">Individual loan discharge events with clawback amounts</p>
        </div>

        {refinanceEvents.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900 border-b border-gray-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Loan ID</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Lender</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Settlement</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Discharged</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Loan Age</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Clawback</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {refinanceEvents.slice(0, 50).map((event, idx) => (
                  <tr key={idx} className={`hover:bg-gray-700 transition-colors ${event.isClawbackPeriod ? 'bg-red-900/10' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">{event.customerName}</span>
                        {event.isRetained && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                            Retained
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-400 font-mono">{event.loanId}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-300">{event.lender || '-'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-300">{event.settlementDate}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-300">{event.dischargeDate}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-300">{event.monthsToDischarge} mo</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {event.isClawbackPeriod ? (
                        <span className="inline-flex items-center text-sm text-red-400">
                          Clawback Period
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-sm text-green-400">
                          Mature
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-orange-400">
                        {formatCurrency(event.clawbackAmountCents)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            No discharge events detected for the selected filters
          </div>
        )}

        {refinanceEvents.length > 50 && (
          <div className="p-4 bg-gray-900 border-t border-gray-700 text-center text-sm text-gray-400">
            Showing 50 of {refinanceEvents.length} events
          </div>
        )}
      </div>

      {/* Info Notice */}
      <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-5">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-semibold text-blue-300 mb-1">
              How This Works
            </h3>
            <div className="text-sm text-blue-200">
              <p>
                This report tracks <strong>loan discharges</strong> from your commission data and splits them
                into two outcomes. A <strong>Retained</strong> discharge is a client kept via a superseding
                loan (a confirmed refinance match) - not a loss. <strong>True churn / Lost</strong> is a
                discharge with no such match: the client genuinely left. Churn Rate, Net Book Growth and
                Clawback Risk count true churn only; retained clients are excluded.
              </p>
              <p className="mt-2">
                <strong>Clawback Period</strong> = Discharge within 24 months of settlement (triggers commission clawback).
                <strong> Mature</strong> = Discharge after 24 months (no clawback impact, but trail revenue stops).
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookHealthReportPage;
